/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Interactive prompt utilities for user confirmations
 */
import * as readline from 'readline';
import pc from 'picocolors';

/**
 * Ask a yes/no question
 */
export const confirm = async (question: string, defaultValue = true): Promise<boolean> => {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  const defaultHint = defaultValue ? '[Y/n]' : '[y/N]';
  const prompt = `${pc.cyan('?')} ${question} ${pc.gray(defaultHint)} `;

  return new Promise((resolve) => {
    rl.question(prompt, (answer) => {
      rl.close();
      
      const cleaned = answer.trim().toLowerCase();
      if (cleaned === '') {
        resolve(defaultValue);
      } else if (cleaned === 'y' || cleaned === 'yes') {
        resolve(true);
      } else if (cleaned === 'n' || cleaned === 'no') {
        resolve(false);
      } else {
        resolve(defaultValue);
      }
    });
  });
};

/**
 * Ask user to select from a list of options
 */
export const select = async <T extends string>(
  question: string,
  options: T[],
  defaultIndex = 0
): Promise<T> => {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  console.log(pc.cyan(`? ${question}`));
  options.forEach((option, index) => {
    const marker = index === defaultIndex ? pc.green('❯') : ' ';
    console.log(`  ${marker} ${index + 1}. ${option}`);
  });

  return new Promise((resolve) => {
    rl.question(pc.gray(`Select (1-${options.length}, default ${defaultIndex + 1}): `), (answer) => {
      rl.close();
      
      const cleaned = answer.trim();
      if (cleaned === '') {
        resolve(options[defaultIndex]);
      } else {
        const index = parseInt(cleaned, 10) - 1;
        if (index >= 0 && index < options.length) {
          resolve(options[index]);
        } else {
          resolve(options[defaultIndex]);
        }
      }
    });
  });
};

/**
 * Show a spinner for async operations
 */
export const spinner = (text: string) => {
  const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
  let currentFrame = 0;
  let intervalId: NodeJS.Timeout | null = null;

  const start = () => {
    intervalId = setInterval(() => {
      process.stdout.write(`\r${pc.cyan(frames[currentFrame])} ${text}`);
      currentFrame = (currentFrame + 1) % frames.length;
    }, 80);
  };

  const stop = (finalText?: string) => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    process.stdout.write(`\r${finalText || text}\n`);
  };

  const succeed = (message?: string) => {
    stop(`${pc.green('✓')} ${message || text}`);
  };

  const fail = (message?: string) => {
    stop(`${pc.red('✗')} ${message || text}`);
  };

  return { start, stop, succeed, fail };
};

